import React, { useState } from "react";
import {
  TextField,
  Button,
  Container,
  Typography,
  Paper,
  Box,
} from "@mui/material";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Styles from "../../styles/_global/Styles";
import { isValidPassword } from "../../utils/Password";

const PasswordReset = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showReset, setShowReset] = useState(false);  // State to show the reset button

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError(""); // Clear previous errors

    if (!isValidPassword(password)) {
      setError(
        "Password must be at least 8 characters long and include a special character."
      );
      setSubmitting(false);
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      setSubmitting(false);
      return;
    }

    const token = window.location.search.split("=")[1]; // URL format is /reset-password?token=xxxx
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/reset-password`,
        {
          token,
          password,
        }
      );
      setMessage(response.data.message);
      setError("");
      setIsSuccess(true);
    } catch (err) {
      setError(err.response.data.error || "An error occurred");
      setMessage("");
      if (err.response.data.error.includes('token')) {
        setShowReset(true); // Show reset button if token error
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    navigate("/forgot-password"); // Navigate to forgot password page
  };

  return (
    <Container
      component="main"
      maxWidth="sm"
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        minWidth: "100vw",
        backgroundColor: "#f4f4f4",
        backgroundImage: 'url("/assets/bg-repeat-01.png")',
        backgroundRepeat: "repeat",
      }}
    >
      <img
        src="/assets/logo-dark.svg"
        alt="Logo"
        style={{ marginBottom: 20, width: 200 }}
      />
      <Paper
        elevation={3}
        sx={{
          padding: 4,
          borderRadius: 3,
          width: "100%",
          maxWidth: "400px",
          boxShadow:
            "0px 4px 15px rgba(0, 0, 0, 0.05), 0px 2px 6px rgba(0, 0, 0, 0.05)",
        }}
      >
        <Box display="flex" flexDirection="column" alignItems="center">
          <Typography component="h1" variant="h5">
            Reset Your Password
          </Typography>
          {!isSuccess ? (
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{ mt: 2, width: "100%" }}
            >
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="New Password"
                type="password"
                id="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  style: { borderRadius: 8 },
                }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="confirmPassword"
                label="Confirm New Password"
                type="password"
                id="confirmPassword"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                InputProps={{
                  style: { borderRadius: 8 },
                }}
              />
              {error && (
                <Typography color="error" sx={{ mt: 2 }}>
                  {error}
                </Typography>
              )}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  marginTop: 3,
                  marginBottom: 2,
                  padding: "10px 0",
                  borderRadius: 8,
                  backgroundColor: Styles.primary.main,
                  "&:hover": {
                    backgroundColor: Styles.secondary.main,
                  },
                  textTransform: "none",
                  color: "#fff",
                }}
                disabled={submitting}
              >
                {submitting ? "Resetting..." : "Reset Password"}
              </Button>
              {showReset && (
                <Button
                  onClick={handleReset}
                  fullWidth
                  variant="text"
                  sx={{
                    mt: 2,
                    color: Styles.primary.main,
                    textTransform: "none",
                  }}
                >
                  Try resetting your password again
                </Button>
              )}
            </Box>
          ) : (
            <>
              <Typography color="primary" sx={{ mt: 2, mb: 2 }}>
                {message}
              </Typography>
              <Button
                onClick={() => navigate("/signin")}
                variant="contained"
                sx={{
                  mt: 2,
                  backgroundColor: Styles.primary.main,
                  "&:hover": {
                    backgroundColor: Styles.secondary.main,
                  },
                  textTransform: "none",
                  color: "#fff",
                }}
              >
                Sign In
              </Button>
            </>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default PasswordReset;
